<template>
  <b-container>
    <!-- Modales como componentes separados -->
    <CreateModal 
      v-model="modalCrear" 
      @created="onEscuelaCreated" 
    />
    
    <EditModal 
      v-model="modalEditar" 
      :escuela="escuelaParaEditar"
      @updated="onEscuelaUpdated" 
    />
    
    <DeleteModal 
      v-model="modalEliminar" 
      :escuela="escuelaParaEliminar"
      @deleted="onEscuelaDeleted" 
    />

    <b-row>
      <b-col cols="12">
        <div class="card card-block">
          <div class="card-body p-2">
            <div class="d-flex justify-content-between align-items-start p-2">
              <b-jumbotron lead="Gestión de Escuelas"></b-jumbotron>
            </div>

            <b-alert
                class="p-3"
                show
                variant="info"
                dismissible>              
              Aquí podrá gestionar las escuelas del sistema, realizar altas, bajas y modificaciones.
            </b-alert>

            <b-row class="d-flex align-items-end justify-content-end my-3">
              <div class="col-sm-12 text-right">
                <b-button
                    class="ml-4 btn-studioAPP"
                    variant="outline-warning"
                    @click="abrirModalCrear">
                  Nueva escuela
                </b-button>
              </div>
            </b-row>

            <div>
              <b-row class="d-flex align-items-end justify-content-end my-1 mx-2">
                <!-- Filtros -->
                <div class="col-sm-12 col-md-3 my-2">
                  <b-form-input 
                    v-model="filters.search" 
                    placeholder="Buscar por nombre..."
                    @keyup.enter="buscarEscuelas"
                  />
                </div>
                <div class="col-sm-12 col-md-2 my-2">
                  <b-form-input 
                    v-model="filters.ciudad" 
                    placeholder="Ciudad..."
                    @keyup.enter="buscarEscuelas"
                  />
                </div>
                <div class="col-sm-12 col-md-2 my-2">
                  <b-form-select
                    v-model="filters.estatus"
                    :options="estatusOptions"
                    @change="buscarEscuelas"
                  />
                </div>
                <div class="col-sm-12 col-md-2 my-2">
                  <b-form-select
                    v-model="filters.per_page"
                    :options="perPageOptions"
                    @change="buscarEscuelas"
                  />
                </div>
                <div class="col-sm-12 col-md-3 my-2 d-flex gap-2">
                  <b-button 
                    variant="primary" 
                    @click="buscarEscuelas"
                    class="mr-2 flex-grow-1"
                  >
                    <i class="ri-search-line"></i> Buscar
                  </b-button>
                  <b-button 
                    variant="outline-secondary" 
                    @click="limpiarFiltros"
                  >
                    <i class="ri-refresh-line"></i> Limpiar
                  </b-button>
                </div>
              </b-row>

              <!-- Tabla -->
              <div class="table-responsive">
                <b-table 
                  class="table data-table"
                  striped
                  hover
                  outlined
                  :items="escuelas"
                  :fields="fields"
                  :busy="loading"
                  show-empty
                  small
                >
                <template #table-busy>
                  <div class="text-center text-primary my-2">
                    <b-spinner class="align-middle"></b-spinner>
                    <strong class="ml-2">Cargando...</strong>
                  </div>
                </template>

                <template #empty="scope">
                  <h5 class="text-center">
                    {{ scope.emptyText='Por el momento no hay escuelas registradas.' }}
                  </h5>
                </template>

                <template #emptyfiltered="scope">
                  <h5 class="text-center">
                    {{ scope.emptyFilteredText='No hay registros que concuerden con los parámetros de búsqueda.' }}
                  </h5>
                </template>

                <!-- Total usuarios -->
                <template #cell(total_users)="row">
                  <span>{{ row.item.total_users || 0 }}</span>
                </template>

                <!-- Licencias activas con badge de color -->
                <template #cell(licencias_activas)="row">
                  <span 
                    :class="['badge-licencia', getBadgeVariant(row.item.licencias_activas, row.item.cantidad)]"
                    v-b-tooltip.hover
                    :title="getTooltipMessage(row.item.licencias_activas, row.item.cantidad)"
                  >
                    {{ row.item.licencias_activas || 0 }}
                  </span>
                </template>

                <!-- Estatus -->
                <template #cell(estatus)="row">
                  <div v-if="row.item.estatus === 1">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-check-circle-fill text-success" viewBox="0 0 16 16">
                      <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                    </svg>
                  </div>
                  <div v-else>
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-x-circle-fill text-danger" viewBox="0 0 16 16">
                      <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z"/>
                    </svg>
                  </div>
                </template>

                <!-- Acciones -->
                <template #cell(acciones)="row">
                  <div class="d-flex justify-content-around align-items-center">
                    <b-button 
                      variant="success" 
                      size="sm"
                      @click="editar(row.item)"
                      class="mr-1">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-pencil-square" viewBox="0 0 16 16">
                        <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z"/>
                        <path fill-rule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5v11z"/>
                      </svg>
                    </b-button>
                    <b-button 
                      variant="danger" 
                      size="sm"
                      @click="confirmarEliminar(row.item)">
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-trash-fill" viewBox="0 0 16 16">
                        <path d="M2.5 1a1 1 0 0 0-1 1v1a1 1 0 0 0 1 1H3v9a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V4h.5a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1H10a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1H2.5zm3 4a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 .5-.5zM8 5a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-1 0v-7A.5.5 0 0 1 8 5zm3 .5v7a.5.5 0 0 1-1 0v-7a.5.5 0 0 1 1 0z"/>
                      </svg>
                    </b-button>
                  </div>
                </template>
              </b-table>

              <!-- Paginación -->
              <div class="d-flex justify-content-between row p-2">
                <div class="col-sm-12 col-md-6 d-flex align-items-center">
                  <p class="text-muted mb-0" v-if="totalRows > 0">
                    Mostrando {{ from }} a {{ to }} de {{ totalRows }} escuelas
                  </p>
                </div>
                <div class="col-sm-12 col-md-6 d-flex align-items-center justify-content-end">
                  <b-pagination
                      v-model="currentPage"
                      :total-rows="totalRows"
                      :per-page="perPage"
                      align="fill"
                      size="sm"
                      class="my-0"
                      @change="cambiarPagina"
                  />
                </div>
              </div>
              </div>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import EscuelaService from '@/services/EscuelaService';
import CreateModal from './components/CreateModal.vue';
import EditModal from './components/EditModal.vue';
import DeleteModal from './components/DeleteModal.vue';

export default {
  name: 'EscuelasIndex',
  components: {
    CreateModal,
    EditModal,
    DeleteModal
  },
  data() {
    return {
      escuelas: [],
      loading: false,
      
      // Control de modales
      modalCrear: false,
      modalEditar: false,
      modalEliminar: false,
      
      // Datos para modales
      escuelaParaEditar: null,
      escuelaParaEliminar: null,
      
      // Paginación
      currentPage: 1,
      perPage: 15,
      totalRows: 0,
      from: 0,
      to: 0,
      
      // Filtros
      filters: {
        search: '',
        ciudad: '',
        estatus: '',
        per_page: 15
      },
      
      // Columnas de la tabla
      fields: [
        { key: 'id', label: 'ID', sortable: true },
        { key: 'nombre', label: 'Nombre', sortable: true },
        { key: 'domain', label: 'Dominio', sortable: true },
        { key: 'ciudad', label: 'Ciudad', sortable: true },
        { key: 'cantidad', label: 'Total licencias', sortable: true, class: 'text-center' },
        { key: 'total_users', label: 'Total usuarios', sortable: true, class: 'text-center' },
        { key: 'licencias_activas', label: 'Licencias activas', sortable: true, class: 'text-center' },
        { key: 'estatus', label: 'Estatus', sortable: true, class: 'text-center' },
        { key: 'acciones', label: 'Acciones', class: 'text-center' }
      ],
      estatusOptions: [
        { value: '', text: 'Todos los estatus' },
        { value: 1, text: 'Activas' },
        { value: 0, text: 'Inactivas' }
      ],
      estatusOptionsForm: [
        { value: 1, text: 'Activa' },
        { value: 0, text: 'Inactiva' }
      ],
      perPageOptions: [
        { value: 10, text: '10 por página' },
        { value: 15, text: '15 por página' },
        { value: 25, text: '25 por página' },
        { value: 50, text: '50 por página' },
        { value: 100, text: '100 por página' }
      ]
    };
  },
  mounted() {
    this.cargarEscuelas();
  },
  methods: {
    async cargarEscuelas(page = 1) {
      this.loading = true;
      try {
        const params = {
          page: page,
          per_page: this.filters.per_page
        };

        // Solo agregar filtros si tienen valor real (no vacío)
        if (this.filters.search && this.filters.search.trim() !== '') {
          params.search = this.filters.search.trim();
        }
        if (this.filters.ciudad && this.filters.ciudad.trim() !== '') {
          params.ciudad = this.filters.ciudad.trim();
        }
        if (this.filters.estatus !== '') {
          params.estatus = this.filters.estatus;
        }

        const response = await EscuelaService.getEscuelas(params);
        
        if (response.data && response.data.resultSet) {
          const resultSet = response.data.resultSet;
          
          // Verificar si resultSet tiene estructura de paginación
          if (resultSet.data && Array.isArray(resultSet.data)) {
            // Respuesta paginada
            this.escuelas = resultSet.data;
            this.currentPage = resultSet.current_page || 1;
            this.perPage = parseInt(resultSet.per_page) || this.filters.per_page;
            this.totalRows = resultSet.total || 0;
            this.from = resultSet.from || 0;
            this.to = resultSet.to || 0;
          } else if (Array.isArray(resultSet)) {
            // Si resultSet es un array directo
            this.escuelas = resultSet;
            this.totalRows = resultSet.length;
            this.from = resultSet.length > 0 ? 1 : 0;
            this.to = resultSet.length;
            this.perPage = this.filters.per_page;
          } else {
            // No hay datos
            this.escuelas = [];
            this.totalRows = 0;
            this.from = 0;
            this.to = 0;
          }
        } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
          // Fallback: respuesta con data directamente
          this.escuelas = response.data.data;
          this.totalRows = response.data.data.length;
          this.from = 1;
          this.to = response.data.data.length;
        } else {
          // No hay datos
          this.escuelas = [];
          this.totalRows = 0;
          this.from = 0;
          this.to = 0;
        }
      } catch (error) {
        console.error('Error al cargar escuelas:', error);
        this.escuelas = [];
        this.$bvToast.toast('Error al cargar las escuelas', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.loading = false;
      }
    },
    getBadgeVariant(activas, total) {
      // Gris: cuando ambos son 0
      if (activas === 0 && total === 0) {
        return 'badge-gris';
      }
      
      // Si no hay total, gris
      if (!total || total === 0) {
        return 'badge-gris';
      }
      
      const porcentaje = (activas / total) * 100;
      
      // Rojo: mayor o igual al 100%
      if (porcentaje >= 100) {
        return 'badge-rojo';
      }
      
      // Naranja: mayor o igual al 90%
      if (porcentaje >= 90) {
        return 'badge-naranja';
      }
      
      // Amarillo: mayor al 50%
      if (porcentaje > 50) {
        return 'badge-amarillo';
      }
      
      // Verde: menor o igual al 50%
      return 'badge-verde';
    },
    getTooltipMessage(activas, total) {
      // Sin licencias
      if (activas === 0 && total === 0) {
        return 'Sin licencias asignadas';
      }
      
      if (!total || total === 0) {
        return 'Sin licencias totales';
      }
      
      const porcentaje = (activas / total) * 100;
      const porcentajeFormateado = porcentaje.toFixed(1);
      
      if (porcentaje >= 100) {
        return `Sobrepasado: ${porcentajeFormateado}% (${activas}/${total})`;
      }
      
      if (porcentaje >= 90) {
        return `Uso crítico: ${porcentajeFormateado}% (${activas}/${total})`;
      }
      
      if (porcentaje > 50) {
        return `Uso alto: ${porcentajeFormateado}% (${activas}/${total})`;
      }
      
      return `Uso normal: ${porcentajeFormateado}% (${activas}/${total})`;
    },
    buscarEscuelas() {
      this.currentPage = 1;
      this.cargarEscuelas(1);
    },
    limpiarFiltros() {
      this.filters.search = '';
      this.filters.ciudad = '';
      this.filters.estatus = '';
      this.filters.per_page = 15;
      this.buscarEscuelas();
    },
    abrirModalCrear() {
      this.modalCrear = true;
    },
    cambiarPagina(page) {
      this.currentPage = page;
      this.cargarEscuelas(page);
    },
    editar(escuela) {
      this.escuelaParaEditar = escuela;
      this.modalEditar = true;
    },
    confirmarEliminar(escuela) {
      this.escuelaParaEliminar = escuela;
      this.modalEliminar = true;
    },
    
    // Métodos para manejar eventos de los modales
    onEscuelaCreated() {
      this.cargarEscuelas(this.currentPage);
    },
    onEscuelaUpdated() {
      this.cargarEscuelas(this.currentPage);
    },
    onEscuelaDeleted() {
      this.cargarEscuelas(this.currentPage);
    }
  }
};
</script>

<style scoped>
.btn-studioAPP {
  font-weight: 600;
}

/* Badges personalizados para licencias */
.badge-licencia {
  display: inline-block;
  padding: 0.5rem 1rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 0.875rem;
  text-align: center;
  min-width: 45px;
  color: #fff;
}

/* Gris - Sin licencias */
.badge-gris {
  background-color: #6c757d;
}

/* Verde - Uso bajo (<75%) */
.badge-verde {
  background-color: #28a745;
}

/* Amarillo - Uso alto (≥75%) */
.badge-amarillo {
  background-color: #ffc107;
  color: #212529;
}

/* Naranja - Uso crítico (≥90%) */
.badge-naranja {
  background-color: #fd7e14;
}

/* Rojo - Sobrepasado (>100%) */
.badge-rojo {
  background-color: #dc3545;
}
</style>
